import React, { createContext, useContext, useState, ReactNode } from 'react';
import { API_BASE_URL } from '../../config/api.config';

// Payment Types
export type PaymentMethod = 'stripe' | 'paypal' | 'bank_transfer';
export type PaymentStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

export interface PaymentIntent {
  id: string;
  amount: number;
  currency: string;
  method: PaymentMethod;
  status: PaymentStatus;
  bookingId?: string;
  clientSecret?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaymentContextType {
  currentPayment: PaymentIntent | null;
  paymentHistory: PaymentIntent[];
  isProcessing: boolean;
  error: string | null;
  initializePayment: (amount: number, bookingId: string, method: PaymentMethod) => Promise<PaymentIntent>;
  processPayment: (paymentIntent: PaymentIntent, paymentDetails: any) => Promise<void>;
  cancelPayment: (paymentId: string) => Promise<void>;
  getPaymentStatus: (paymentId: string) => Promise<PaymentStatus>;
  clearError: () => void;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

interface PaymentProviderProps {
  children: ReactNode;
}

export const PaymentProvider: React.FC<PaymentProviderProps> = ({ children }) => {
  const [currentPayment, setCurrentPayment] = useState<PaymentIntent | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentIntent[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initializePayment = async (
    amount: number,
    bookingId: string,
    method: PaymentMethod
  ): Promise<PaymentIntent> => {
    setIsProcessing(true);
    setError(null);

    try {
      // API call to backend to create payment intent
      const response = await fetch(`${API_BASE_URL}/payment/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          amount,
          bookingId,
          method,
          currency: 'EUR'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to initialize payment');
      }

      const paymentIntent = await response.json();
      setCurrentPayment(paymentIntent);
      return paymentIntent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment initialization failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const processPayment = async (paymentIntent: PaymentIntent, paymentDetails: any): Promise<void> => {
    setIsProcessing(true);
    setError(null);

    try {
      // API call to process payment
      const response = await fetch(`${API_BASE_URL}/payment/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          paymentDetails
        })
      });

      if (!response.ok) {
        throw new Error('Payment processing failed');
      }

      const updatedPayment = await response.json();
      setCurrentPayment(updatedPayment);
      setPaymentHistory([...paymentHistory, updatedPayment]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment processing failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelPayment = async (paymentId: string): Promise<void> => {
    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/payment/${paymentId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel payment');
      }

      setCurrentPayment(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment cancellation failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const getPaymentStatus = async (paymentId: string): Promise<PaymentStatus> => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment/${paymentId}/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment status');
      }

      const { status } = await response.json();
      return status;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get payment status';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const clearError = () => setError(null);

  const value: PaymentContextType = {
    currentPayment,
    paymentHistory,
    isProcessing,
    error,
    initializePayment,
    processPayment,
    cancelPayment,
    getPaymentStatus,
    clearError
  };

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

export default PaymentProvider;
